import React from 'react'
import { useTranslation } from 'react-i18next'

/** Filenames in `public/assets/roadmap icons/` (emoji .png), order = stages 1–5 */
const ROADMAP_ICON_EMOJIS = ['🔍', '📚', '💚', '🌙', '✨'] as const

function roadmapIconSrc(emoji: (typeof ROADMAP_ICON_EMOJIS)[number]): string {
  const dir = '/assets/roadmap%20icons'
  return `${dir}/${encodeURIComponent(emoji)}.png`
}

interface RoadmapStage {
  id: number
  title: string
  description: string
  iconEmoji: (typeof ROADMAP_ICON_EMOJIS)[number]
  iconAlt: string
}

export default function IslamRoadmap() {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language || 'ar'
  const isRTL = currentLang === 'ar'

  const stages: RoadmapStage[] = [
    {
      id: 1,
      title: t('islam.roadmap.stage1.title', 'الاكتشاف'),
      description: t('islam.roadmap.stage1.desc', 'التعرف على معنى الإسلام'),
      iconEmoji: ROADMAP_ICON_EMOJIS[0],
      iconAlt: t('islam.roadmap.stage1.title', 'الاكتشاف'),
    },
    {
      id: 2,
      title: t('islam.roadmap.stage2.title', 'البحث'),
      description: t('islam.roadmap.stage2.desc', 'دراسة المصادر الموثوقة'),
      iconEmoji: ROADMAP_ICON_EMOJIS[1],
      iconAlt: t('islam.roadmap.stage2.title', 'البحث'),
    },
    {
      id: 3,
      title: t('islam.roadmap.stage3.title', 'الطمأنينة'),
      description: t('islam.roadmap.stage3.desc', 'الوصول إلى السكينة والقناعة'),
      iconEmoji: ROADMAP_ICON_EMOJIS[2],
      iconAlt: t('islam.roadmap.stage3.title', 'الطمأنينة'),
    },
    {
      id: 4,
      title: t('islam.roadmap.stage4.title', 'الشهادتان'),
      description: t('islam.roadmap.stage4.desc', 'النطق بالشهادتين'),
      iconEmoji: ROADMAP_ICON_EMOJIS[3],
      iconAlt: t('islam.roadmap.stage4.title', 'الشهادتان'),
    },
    {
      id: 5,
      title: t('islam.roadmap.stage5.title', 'البدء'),
      description: t('islam.roadmap.stage5.desc', 'بداية رحلة المسلم الجديد'),
      iconEmoji: ROADMAP_ICON_EMOJIS[4],
      iconAlt: t('islam.roadmap.stage5.title', 'البدء'),
    },
  ]

  const roadmapIcon = (stage: RoadmapStage, sizeClass: string) => (
    <img
      src={roadmapIconSrc(stage.iconEmoji)}
      alt={stage.iconAlt}
      className={`${sizeClass} object-contain select-none`}
      loading="lazy"
      decoding="async"
    />
  )

  return (
    <section className="py-8 md:py-10 bg-white dark:bg-gray-900">
      {/* Roadmap Section Container */}
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Light Green Card Container - Figma Design */}
        <div className="bg-gradient-to-br from-[#EAF7F2] to-[#DFF3EA] dark:bg-gray-800 rounded-2xl p-6 md:p-8" style={{ boxShadow: '0 10px 24px rgba(16, 24, 40, 0.10)', border: '1px solid rgba(16, 24, 40, 0.04)' }}>
          {/* Title and Subtitle - Centered */}
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-3" style={{ fontWeight: 700 }}>
              {t('islam.roadmap.title', 'Roadmap to embracing Islam')}
            </h2>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" style={{ color: '#6B7280' }}>
              {t('islam.roadmap.subtitle', 'A blessed spiritual journey towards knowing the truth and embracing Islam')}
            </p>
          </div>

          {/* Desktop: icon row + labels row so the connector sits exactly on circle centers */}
          <div className="hidden md:block py-4 md:py-6">
            <div className="relative flex justify-between items-center mb-6 md:mb-8">
              <div
                className="pointer-events-none absolute left-[10%] right-[10%] top-1/2 z-0 h-[3px] -translate-y-1/2 rounded-full bg-primary-600 dark:bg-primary-500"
                aria-hidden
              />
              {stages.map((stage) => (
                <div
                  key={`icon-${stage.id}`}
                  className="relative z-10 flex flex-1 justify-center min-w-0 max-w-[200px]"
                >
                  <div className="relative">
                    <div
                      className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-[3px] border-primary-600 bg-white p-2 dark:border-primary-500 dark:bg-gray-700"
                      style={{ boxShadow: '0 4px 12px rgba(16, 24, 40, 0.06)' }}
                    >
                      {roadmapIcon(stage, 'h-10 w-10 md:h-11 md:w-11')}
                    </div>
                    <div
                      className={`absolute -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[9px] font-bold text-white dark:bg-primary-500 ${isRTL ? '-left-0.5' : '-right-0.5'}`}
                    >
                      {stage.id}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between">
              {stages.map((stage) => (
                <div
                  key={`text-${stage.id}`}
                  className="flex min-w-0 max-w-[200px] flex-1 flex-col items-center px-1 text-center"
                >
                  <h3 className="mb-2 text-base font-bold text-gray-900 dark:text-white" style={{ fontWeight: 600 }}>
                    {stage.title}
                  </h3>
                  <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>
                    {stage.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: Vertical Timeline */}
          <div className="md:hidden space-y-6">
            {stages.map((stage, index) => (
              <div key={stage.id} className="flex items-start gap-3">
                {/* Circle */}
                <div className="relative flex-shrink-0">
                  <div
                    className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-[3px] border-primary-600 bg-white p-1.5 dark:border-primary-500 dark:bg-gray-700"
                    style={{ boxShadow: '0 4px 12px rgba(16, 24, 40, 0.06)' }}
                  >
                    {roadmapIcon(stage, 'h-9 w-9')}
                  </div>
                  <div
                    className={`absolute top-0 flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[9px] font-bold text-white dark:bg-primary-500 ${isRTL ? 'left-0' : 'right-0'}`}
                  >
                    {stage.id}
                  </div>
                  {index < stages.length - 1 && (
                    <div
                      className={`absolute top-16 h-10 w-[3px] rounded-full bg-primary-600 dark:bg-primary-500 ${
                        isRTL ? 'right-1/2 translate-x-1/2' : 'left-1/2 -translate-x-1/2'
                      }`}
                      aria-hidden
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pt-1">
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1" style={{ fontWeight: 600 }}>
                    {stage.title}
                  </h3>
                  <p className="text-xs" style={{ color: '#6B7280' }}>
                    {stage.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

